import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { updateUserMedData, fetchMedlists } from '../../store/meddata/meddataSlice';
import { CHECK_STATUS_FINAL_MESSAGE, isCheckStatusFinal } from '../../utils/checkStatusErrors';
import { fullName } from '../../utils/utils';
import { formatBackendTime, formatDateForInput } from '../../utils/date.utils';
import { CheckStatus } from '../../types/type';
import { MedicalService } from '../../services/medical.service';

type ValidationErrors = {
  temperature: string;
  bloodPressure: string;
  pulse: string;
  promile: string;
};

const validateTemperature = (value: number) => {
  if (!value) return 'Температура обязательна.';
  if (value < 35 || value > 42) return 'Температура должна быть в диапазоне от 35 до 42.';
  return '';
};

const validatePulse = (value: number) => {
  if (!value) return 'Пульс обязателен.';
  if (value > 180 || value < 40) return 'Пульс должен быть в диапазоне от 40 до 180.';
  return '';
};

const validatePromile = (value: number) => {
  if (value === null || value === undefined) return 'Промиле обязательен.';
  if (value < 0) return 'Промиле не может быть отрицательным.';
  return '';
};

const validateBloodPressure = (value: string) => {
  const bloodPressureRegex = /^\d{2,3}\/\d{2,3}$/;
  if (!bloodPressureRegex.test(value)) return 'Давлению должно быть в формате XX/YY или XXX/YYY.';
  return '';
};

export const useMedCard = () => {
  const user = useAppSelector((state: RootState) => state.user.user);
  const { data, loading } = useSelector((state: RootState) => state.meddata);
  const reduxMedLists = useAppSelector((state) => state.meddata.medicLists);

  const dispatch = useAppDispatch();

  const [editable, setEditable] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState<boolean>(false);
  const [reasonForRefusal, setReasonForRefusal] = useState<string>('');
  const [error, setError] = useState<ValidationErrors>({
    temperature: '',
    bloodPressure: '',
    pulse: '',
    promile: '',
  });
  const [formData, setFormData] = useState({
    date: '',
    status: '',
    fullName: '',
    time: '',
    sex: '',
    birthDate: '',
    temperature: 0,
    bloodPressure: '',
    avatar: '',
    num: 0,
    selfie: '',
    pulse: 0,
    promile: 0,
    lament: false,
    autopark: '',
    driverProfileId: '',
  });

  const [debouncedFormData, setDebouncedFormData] = useState(formData);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true); // Флаг для отслеживания первого рендера

  useEffect(() => {
    const medlistByDriver = reduxMedLists?.find((item) => item.id === data?.id);

    if (data) {
      setFormData({
        autopark: medlistByDriver?.autopark?.legalName || '',
        status: data?.status || '',
        date: formatDateForInput(data?.checkTime),
        time: formatBackendTime(data?.checkTime),
        birthDate: formatDateForInput(medlistByDriver?.driver?.birthDate),
        temperature: data?.bodyTemperature || 0,
        bloodPressure: data?.bloodPressure || '',
        avatar: medlistByDriver?.driverProfile?.avatar || '',
        num: data?.num || 0,
        selfie: data?.selfie || '',
        pulse: data?.heartRate || 0,
        promile: data?.bloodAlcohol || 0,
        fullName: fullName(medlistByDriver?.driverProfile),
        sex: medlistByDriver?.driver?.sex || '',
        lament: data?.symptomsPresent,
        driverProfileId: medlistByDriver?.driverProfile?.id || '',
      });
    }
  }, [data]);

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setDebouncedFormData(formData);
    }, 1000);

    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [formData]);

  const handleChange = (id: string, value: number | string) => {
    let validationError = '';
    if (id === 'temperature') {
      validationError = validateTemperature(Number(value));
    } else if (id === 'bloodPressure') {
      validationError = validateBloodPressure(value as string);
    } else if (id === 'pulse') {
      validationError = validatePulse(Number(value));
    } else if (id === 'promile') {
      validationError = validatePromile(Number(value));
    }

    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));

    setError((prevErrors) => ({
      ...prevErrors,
      [id]: validationError,
    }));
  };

  const handleStatusChange = async (newStatus: CheckStatus) => {
    // Показываем модальное окно для обоснования при отрицательном решении
    if (newStatus === 'rejected') {
      setShowReasonModal(true);
      return;
    }

    try {
      await MedicalService.updateMedListStatus({ id: data?.id ?? 0, status: newStatus, medicID: user.id });
      // Обновляем данные после изменения статуса
      dispatch(fetchMedlists({ id: data?.id ?? 0 }));
    } catch (error) {
      if (isCheckStatusFinal(error)) {
        alert(CHECK_STATUS_FINAL_MESSAGE);
        dispatch(fetchMedlists({ id: data?.id ?? 0 }));
      } else {
        console.error('Ошибка при обновлении статуса:', error);
      }
    }
  };

  // Обработчик отправки обоснования отказа
  const handleReasonSubmit = async () => {
    if (data?.id && reasonForRefusal) {
      try {
        await MedicalService.updateMedListStatus({
          id: data.id,
          status: 'rejected',
          medicID: user.id,
          reasonForRefusal,
        });
        // Обновляем данные после изменения статуса
        dispatch(fetchMedlists({ id: data.id }));
        setShowReasonModal(false);
        setReasonForRefusal('');
      } catch (error) {
        if (isCheckStatusFinal(error)) {
          alert(CHECK_STATUS_FINAL_MESSAGE);
          dispatch(fetchMedlists({ id: data.id }));
          setShowReasonModal(false);
        } else {
          console.error('Ошибка при обновлении статуса с причиной отказа:', error);
        }
      }
    }
  };

  const isDataChanged =
    debouncedFormData.temperature !== data?.bodyTemperature ||
    debouncedFormData.bloodPressure !== data?.bloodPressure ||
    debouncedFormData.pulse !== data?.heartRate ||
    debouncedFormData.promile !== data?.bloodAlcohol;

  const handleUpdateUserData = useCallback(async () => {
    if (isDataChanged) {
      dispatch(
        updateUserMedData({
          id: data?.id ?? 0,
          updates: {
            bodyTemperature: Number(debouncedFormData.temperature),
            bloodPressure: debouncedFormData.bloodPressure,
            heartRate: Number(debouncedFormData.pulse),
            bloodAlcohol: Number(debouncedFormData.promile),
          },
        }),
      );
    }
  }, [debouncedFormData, data]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (debouncedFormData && isDataChanged) {
      handleUpdateUserData();
    }
  }, [debouncedFormData]);

  return {
    formData,
    error,
    editable,
    loading,
    debouncedFormData,
    showReasonModal,
    reasonForRefusal,
    handleChange,
    handleStatusChange,
    handleUpdateUserData,
    handleReasonSubmit,
    setEditable,
    setShowReasonModal,
    setReasonForRefusal,
  };
};
